import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axios';

interface UseFetchOptions {
    endpoint: string;
    immediate?: boolean;
    enabled?: boolean;
}

export function useFetch<T>(options: UseFetchOptions) {
    const { endpoint, immediate = true, enabled = true } = options;
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(immediate && enabled);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        if (!enabled) return;

        setLoading(true);
        try {
            const response = await axiosInstance.get<T>(endpoint);
            setData(response.data);
            setError('');
        } catch (err: unknown) {
            console.error(`[useFetch] ${endpoint}:`, err);
            const apiError =
                err && typeof err === 'object' && 'error' in err
                    ? (err as { error: string }).error
                    : null;
            setError(
                apiError ?? (err instanceof Error ? err.message : `Failed to fetch data from ${endpoint}`)
            );
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [endpoint, enabled]);

    useEffect(() => {
        if (immediate && enabled) {
            fetchData();
        }
    }, [immediate, enabled, fetchData]);

    return { data, loading, error, refetch: fetchData, setData, setError };
}
